import React, { useState, useMemo } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaSitemap,
  FaBolt,
  FaSlidersH,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { formatDisplayName } from "../../../utils/textUtils";

const glassPanel = {
  background: "rgba(13,21,38,0.78)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow:
    "0 12px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
};

interface SiteSidebarProps {
  sites: any[];
  isPending: boolean;
  selectedSiteId: string | null;
  setSelectedSiteId: (id: string) => void;
  setSelectedSystemId: (id: string | null) => void;
  page: number;
  totalPages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onEditCriticalValue?: (site: any) => void;
  onSelectSiteCallback?: (site: any) => void;
}

const SiteSidebar: React.FC<SiteSidebarProps> = ({
  sites,
  isPending,
  selectedSiteId,
  setSelectedSiteId,
  setSelectedSystemId,
  page,
  totalPages,
  setPage,
  onEditCriticalValue,
  onSelectSiteCallback,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredSites = useMemo(() => {
    if (!searchQuery.trim()) return sites;
    const query = searchQuery.toLowerCase().trim();
    return sites.filter((site: any) => {
      const name = (site.name || "").toLowerCase();
      const desc = (site.description || "").toLowerCase();
      const extId = String(site.siteExternalId || "");
      return name.includes(query) || desc.includes(query) || extId.includes(query);
    });
  }, [sites, searchQuery]);

  return (
    <aside
      className="w-full xl:w-80 2xl:w-[340px] flex-shrink-0 flex flex-col rounded-2xl overflow-hidden min-h-[380px] xl:min-h-0 xl:h-full transition-all duration-300"
      style={glassPanel}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(59,130,246,0.2) 0%, rgba(99,102,241,0.15) 100%)",
              border: "1px solid rgba(59,130,246,0.3)",
              boxShadow: "0 0 16px rgba(59,130,246,0.2)",
            }}
          >
            <FaSitemap className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-bold text-white tracking-tight">
              Sites
            </h2>
            <p className="text-[10px] text-slate-400 font-medium">
              Grounding facility zones
            </p>
          </div>
        </div>

        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full border"
          style={{
            background: "rgba(59,130,246,0.10)",
            border: "1px solid rgba(59,130,246,0.25)",
            color: "#60a5fa",
          }}
        >
          {sites.length} {sites.length === 1 ? "site" : "sites"}
        </span>
      </div>

      {/* Search / Filter box */}
      <div
        className="px-3 sm:px-4 py-2.5 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="relative flex items-center">
          <FaSearch className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
          <input
            type="text"
            placeholder="Search sites…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 text-xs rounded-xl text-white placeholder-slate-500 transition-all outline-none"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "rgba(59,130,246,0.5)";
              e.target.style.boxShadow = "0 0 0 2px rgba(59,130,246,0.15)";
              e.target.style.background = "rgba(255,255,255,0.06)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "rgba(255,255,255,0.08)";
              e.target.style.boxShadow = "none";
              e.target.style.background = "rgba(255,255,255,0.04)";
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 p-1 text-slate-400 hover:text-white transition-colors cursor-pointer"
              title="Clear search"
            >
              <FaTimes className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>

      {/* Site list container */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-3.5 custom-scrollbar min-h-0">
        {isPending && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-slate-400 font-medium">Loading sites…</span>
          </div>
        )}

        {!isPending && sites.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500 text-center px-4">
            <div
              className="p-3.5 rounded-2xl mb-3"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <FaSitemap className="w-8 h-8 opacity-30 text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-slate-400">No sites found</p>
            <p className="text-xs text-slate-600 mt-1 max-w-[200px]">
              No facility sites are registered under this account
            </p>
          </div>
        )}

        {!isPending && sites.length > 0 && filteredSites.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500 text-center px-4">
            <p className="text-xs text-slate-400">No sites match "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-[11px] text-blue-400 hover:underline cursor-pointer"
            >
              Clear filter
            </button>
          </div>
        )}

        <ul className="space-y-2.5">
          {filteredSites.map((site: any) => {
            const active = site.id === selectedSiteId;
            const threshold =
              site.criticalResistanceValue != null
                ? Number(site.criticalResistanceValue).toFixed(1)
                : "3.0";

            return (
              <li
                key={site.id}
                onClick={() => {
                  setSelectedSiteId(site.id);
                  setSelectedSystemId(site.systems?.[0]?.id || null);
                  if (onSelectSiteCallback) onSelectSiteCallback(site);
                }}
                className={`cursor-pointer group relative p-3 sm:p-3.5 rounded-2xl transition-all duration-200 ${
                  active
                    ? "border text-white shadow-lg"
                    : "hover:bg-white/[0.04] border border-white/[0.04] text-slate-300"
                }`}
                style={{
                  background: active
                    ? "linear-gradient(135deg, rgba(59,130,246,0.18) 0%, rgba(99,102,241,0.10) 100%)"
                    : "rgba(255,255,255,0.02)",
                  borderColor: active
                    ? "rgba(59,130,246,0.35)"
                    : "rgba(255,255,255,0.04)",
                  boxShadow: active
                    ? "0 6px 20px rgba(59,130,246,0.18), inset 0 1px 0 rgba(255,255,255,0.1)"
                    : "none",
                }}
                title={site.description || site.name}
              >
                {/* Active indicator bar on left edge */}
                {active && (
                  <div
                    className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
                    style={{
                      background: "linear-gradient(180deg, #38bdf8, #6366f1)",
                      boxShadow: "0 0 8px rgba(56,189,248,0.6)",
                    }}
                  />
                )}

                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    {/* Status dot */}
                    <div className="relative flex-shrink-0">
                      <div
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${
                          active ? "bg-blue-400" : "bg-slate-600 group-hover:bg-slate-500"
                        }`}
                      />
                      {active && (
                        <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-60" />
                      )}
                    </div>

                    {/* Site name */}
                    <span
                      className={`text-xs sm:text-sm font-semibold truncate ${
                        active ? "text-white" : "text-slate-300 group-hover:text-white"
                      }`}
                    >
                      {formatDisplayName(site.name)}
                    </span>
                  </div>

                  {/* Systems count badge */}
                  <span
                    className={`text-[10px] sm:text-[11px] px-2 py-0.5 rounded-full border font-semibold flex-shrink-0 transition-colors ${
                      active
                        ? "bg-blue-500/20 text-blue-300 border-blue-500/35"
                        : "bg-slate-800/80 text-slate-400 border-slate-700/60"
                    }`}
                  >
                    {site.systems?.length || 0} sys
                  </span>
                </div>

                {/* Sub-row: Critical Threshold Pill + Quick Action */}
                <div
                  className="flex items-center justify-between mt-2.5 pt-2.5 gap-2"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
                >
                  <div
                    className={`inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] px-2 py-0.5 rounded-lg border font-mono transition-colors ${
                      active
                        ? "bg-amber-500/15 text-amber-300 border-amber-500/30 shadow-sm"
                        : "bg-black/30 text-amber-400/80 border-white/[0.04]"
                    }`}
                    title={`Critical alert triggers at ${threshold} Ω`}
                  >
                    <FaBolt className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
                    <span>Crit: {threshold} Ω</span>
                  </div>

                  {onEditCriticalValue && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCriticalValue(site);
                      }}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer ${
                        active
                          ? "text-blue-300 hover:text-white bg-blue-500/15 hover:bg-blue-500/30 border border-blue-500/30 hover:border-blue-500/50"
                          : "text-slate-400 hover:text-blue-300 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.06]"
                      }`}
                      title="Set Critical Value for this site"
                    >
                      <FaSlidersH className="w-2.5 h-2.5" />
                      <span>Set Limit</span>
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Pagination Footer */}
      <div
        className="flex justify-between items-center px-4 sm:px-5 py-3 flex-shrink-0"
        style={{
          borderTop: "1px solid rgba(255,255,255,0.07)",
          background: "rgba(0,0,0,0.15)",
        }}
      >
        <button
          onClick={() => setPage(Math.max(page - 1, 0))}
          disabled={page === 0}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent transition-all cursor-pointer"
        >
          <FaChevronLeft className="w-3 h-3" />
          <span className="hidden sm:inline text-[11px]">Prev</span>
        </button>

        <span className="text-[11px] font-semibold text-slate-400 font-mono">
          Page {page + 1} / {totalPages || 1}
        </span>

        <button
          onClick={() => setPage(Math.min(page + 1, totalPages - 1))}
          disabled={page >= totalPages - 1}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent transition-all cursor-pointer"
        >
          <span className="hidden sm:inline text-[11px]">Next</span>
          <FaChevronRight className="w-3 h-3" />
        </button>
      </div>
    </aside>
  );
};

export default React.memo(SiteSidebar);
